import { useState, useCallback, useMemo } from "react";

/**
 * @typedef {Object} ColumnPreferences
 * @property {string[]} visibleKeys - Currently visible column keys
 * @property {(key: string) => void} toggleColumn - Toggle a column's visibility
 * @property {() => void} resetToDefaults - Reset visible keys back to defaults
 * @property {(key: string) => boolean} isVisible - Check if a column key is visible
 */

/**
 * Hook for persisting table column visibility to localStorage.
 * @param {string} storageKey - localStorage key to persist under
 * @param {string[]} defaultVisibleKeys - Keys visible by default
 * @param {string[]} allKeys - All available column keys (used to drop stale stored keys)
 * @returns {ColumnPreferences} Column preference state and controls
 */
export function useColumnPreferences(storageKey, defaultVisibleKeys, allKeys) {
  const allKeysSet = useMemo(() => new Set(allKeys), [allKeys]);

  const [visibleKeys, setVisibleKeys] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          // Drop any stored keys that no longer exist in allKeys
          const valid = parsed.filter((k) => allKeysSet.has(k));
          if (valid.length > 0) return valid;
        }
      }
    } catch {
      // ignore parse errors
    }
    return defaultVisibleKeys;
  });

  const persist = useCallback(
    (keys) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(keys));
      } catch {
        // ignore storage errors (e.g. private browsing quota)
      }
    },
    [storageKey]
  );

  const toggleColumn = useCallback(
    (key) => {
      setVisibleKeys((prev) => {
        const next = prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const resetToDefaults = useCallback(() => {
    setVisibleKeys(defaultVisibleKeys);
    persist(defaultVisibleKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist, storageKey]);

  const isVisible = useCallback(
    (key) => visibleKeys.includes(key),
    [visibleKeys]
  );

  return { visibleKeys, toggleColumn, resetToDefaults, isVisible };
}
